import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { GlassCard } from '@/components/brand/GlassCard';
import { adminListLeads, adminUpdateLead } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const LEADS_TABLE_HEADERS = ['Name', 'Email', 'Subject', 'Source', 'Status', 'Received', ''];
const STATUS_FILTERS = ['', 'new', 'in_progress', 'closed'];
const STATUS_OPTIONS = ['new', 'in_progress', 'closed'];

const statusBadge = (s) => {
    if (s === 'new') return 'border-la-gold/40 text-la-gold bg-la-gold/10';
    if (s === 'in_progress') return 'border-amber-300/30 text-amber-200 bg-amber-200/10';
    if (s === 'closed') return 'border-emerald-400/30 text-emerald-200 bg-emerald-200/10';
    return 'border-white/10 text-la-text2 bg-white/[0.04]';
};

const LeadRow = ({ lead, onAdvance }) => (
    <tr className="border-t border-white/10 hover:bg-white/[0.03] align-top">
        <td className="px-4 py-3 text-la-text">{lead.name}</td>
        <td className="px-4 py-3"><a href={`mailto:${lead.email}`} className="text-la-text2 hover:text-la-text underline-grow">{lead.email}</a></td>
        <td className="px-4 py-3 max-w-md"><div className="text-la-text2">{lead.subject || '—'}</div><div className="mt-1 text-[11px] text-la-text3 line-clamp-2">{lead.message}</div></td>
        <td className="px-4 py-3 text-[11.5px] text-la-text3">{lead.source_page || '—'}</td>
        <td className="px-4 py-3">
            <span className={`text-[10px] tracking-[0.16em] uppercase px-2 py-1 rounded-la-sm border ${statusBadge(lead.status)}`}>{lead.status || 'new'}</span>
        </td>
        <td className="px-4 py-3 text-la-text3">{formatDate(lead.created_at)}</td>
        <td className="px-4 py-3">
            <select
                value={lead.status || 'new'}
                onChange={(e) => onAdvance(lead.lead_id, e.target.value)}
                data-testid="admin-lead-status-select"
                className="bg-white/[0.04] border border-white/10 rounded-la-sm text-[11px] text-la-text2 px-2 py-1 focus:outline-none focus:border-la-gold/40"
            >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
        </td>
    </tr>
);

const LeadsFilter = ({ value, onChange }) => (
    <div className="mb-4 flex items-center gap-2" data-testid="admin-leads-filter">
        <span className="meta-eyebrow">Filter</span>
        {STATUS_FILTERS.map((s) => (
            <button
                key={s || 'all'}
                onClick={() => onChange(s)}
                className={`text-[11px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-la-sm border ${value === s ? 'border-la-gold/40 text-la-gold bg-la-gold/10' : 'border-white/10 text-la-text2 hover:text-la-text'}`}
            >
                {s ? s.replace('_', ' ') : 'All'}
            </button>
        ))}
    </div>
);

const LeadsTable = ({ items, onAdvance }) => (
    <GlassCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
            <table data-testid="admin-table" className="w-full text-sm">
                <thead className="bg-white/[0.04]">
                    <tr className="text-[11px] tracking-[0.14em] uppercase text-la-text3">
                        {LEADS_TABLE_HEADERS.map((h) => (
                            <th key={h || 'actions'} className="text-left px-4 py-3">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr><td colSpan={LEADS_TABLE_HEADERS.length} className="px-4 py-10 text-center text-la-text3">No leads.</td></tr>
                    ) : items.map((l) => <LeadRow key={l.lead_id} lead={l} onAdvance={onAdvance} />)}
                </tbody>
            </table>
        </div>
    </GlassCard>
);

export default function AdminLeadsPage() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        try { setItems(await adminListLeads()); }
        catch { toast.error('Could not load leads.'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const advance = useCallback(async (leadId, newStatus) => {
        try {
            await adminUpdateLead(leadId, { status: newStatus });
            toast.success('Lead updated.');
            load();
        } catch {
            toast.error('Could not update lead.');
        }
    }, [load]);

    const filtered = filter ? items.filter((l) => (l.status || 'new') === filter) : items;

    return (
        <AdminLayout title="Leads">
            <LeadsFilter value={filter} onChange={setFilter} />
            <LeadsTable items={filtered} onAdvance={advance} />
        </AdminLayout>
    );
}
